import { useState } from "react";
import { motion } from "framer-motion";
import { EnvelopeSimple, Phone, MapPin, WhatsappLogo, ArrowRight, Clock, InstagramLogo, FacebookLogo } from "@phosphor-icons/react";
import { toast } from "sonner";
import { SITE, waLink } from "@/lib/site";

const QUICK_TOPICS = [
    "Plan a honeymoon",
    "Family holiday",
    "Solo adventure",
    "Business + leisure",
    "Group travel",
    "Visa assistance",
    "Custom itinerary",
    "Something else",
];

export default function Contact() {
    const [form, setForm] = useState({ name: "", email: "", phone: "", destination: "", dates: "", message: "", topic: "" });
    const [loading, setLoading] = useState(false);

    const onChange = (e) => setForm((f) => ({ ...f, [e.target.name]: e.target.value }));
    const setTopic = (t) => setForm((f) => ({ ...f, topic: f.topic === t ? "" : t }));

    const submit = (e) => {
        e.preventDefault();
        if (!form.name.trim() || !form.email.trim() || !form.message.trim()) {
            toast.error("Please fill in your name, email and a short message.");
            return;
        }
        if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) {
            toast.error("That email doesn't look right.");
            return;
        }
        setLoading(true);
        setTimeout(() => {
            setLoading(false);
            toast.success("Thank you. Our concierge will be in touch within 24 hours.");
            setForm({ name: "", email: "", phone: "", destination: "", dates: "", message: "", topic: "" });
        }, 800);
    };

    return (
        <div data-testid="page-contact" className="pt-28">
            {/* Hero */}
            <section className="py-20 bg-navy">
                <div className="max-w-[1600px] mx-auto px-6 md:px-12 lg:px-24">
                    <p className="overline">Let's talk</p>
                    <h1 className="font-serif text-5xl sm:text-6xl lg:text-7xl font-medium leading-[1.05] mt-6 tracking-tight max-w-4xl">
                        Begin a <em className="italic text-gold">conversation.</em>
                    </h1>
                    <p className="font-sans text-base text-white/65 mt-6 max-w-2xl leading-relaxed">
                        Tell us where you'd like to go — or simply how you'd like to feel. A real concierge will reply
                        within 24 hours.
                    </p>
                </div>
            </section>

            <section className="pb-24 lg:pb-32 bg-navy">
                <div className="max-w-[1600px] mx-auto px-6 md:px-12 lg:px-24 grid lg:grid-cols-12 gap-12">
                    {/* Form */}
                    <motion.form
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.7 }}
                        onSubmit={submit}
                        className="lg:col-span-7 p-8 lg:p-12 border border-white/10 bg-[#0F1730]"
                        data-testid="contact-form"
                    >
                        <p className="overline">Plan your journey</p>
                        <h2 className="font-serif text-3xl sm:text-4xl mt-3">Tell us a little about your trip.</h2>

                        {/* Quick topic selector */}
                        <div className="mt-8">
                            <label className="overline block mb-3">What are you planning?</label>
                            <div className="flex flex-wrap gap-2">
                                {QUICK_TOPICS.map((t) => (
                                    <button
                                        key={t}
                                        type="button"
                                        onClick={() => setTopic(t)}
                                        className={`px-3 py-1.5 text-xs uppercase tracking-widest border transition-colors ${
                                            form.topic === t
                                                ? "bg-gold text-navy border-gold"
                                                : "border-white/20 text-white/60 hover:border-gold hover:text-gold"
                                        }`}
                                    >
                                        {t}
                                    </button>
                                ))}
                            </div>
                        </div>

                        <div className="grid sm:grid-cols-2 gap-x-8 gap-y-8 mt-10">
                            <Field label="Full name" name="name" value={form.name} onChange={onChange} testid="contact-name" required />
                            <Field label="Email" name="email" type="email" value={form.email} onChange={onChange} testid="contact-email" required />
                            <Field label="Phone" name="phone" value={form.phone} onChange={onChange} testid="contact-phone" />
                            <Field label="Destination" name="destination" value={form.destination} onChange={onChange} testid="contact-destination" placeholder="e.g., Maldives" />
                            <div className="sm:col-span-2">
                                <Field label="Tentative dates" name="dates" value={form.dates} onChange={onChange} testid="contact-dates" placeholder="e.g., Mid-April for 7 nights" />
                            </div>
                            <div className="sm:col-span-2">
                                <label className="overline block mb-3">Tell us more</label>
                                <textarea
                                    name="message"
                                    value={form.message}
                                    onChange={onChange}
                                    rows={4}
                                    required
                                    data-testid="contact-message"
                                    placeholder="Number of travelers, interests, how you'd like to feel..."
                                    className="editorial-input w-full resize-none"
                                />
                            </div>
                        </div>

                        <div className="mt-10 flex flex-col sm:flex-row gap-4 sm:items-center">
                            <button
                                type="submit"
                                disabled={loading}
                                data-testid="contact-submit"
                                className="inline-flex items-center justify-center gap-2 bg-gold text-navy hover:bg-[#E5C158] px-8 py-4 font-sans font-medium uppercase tracking-wider text-xs transition-colors disabled:opacity-60"
                            >
                                {loading ? "Sending…" : "Send Inquiry"}
                                <ArrowRight size={14} weight="bold" />
                            </button>
                            <a
                                href={waLink("Hi Midasis! I'd like help planning a trip.")}
                                target="_blank"
                                rel="noreferrer"
                                data-testid="contact-whatsapp-cta"
                                className="inline-flex items-center justify-center gap-2 border border-gold/70 text-gold hover:bg-gold/10 px-8 py-4 font-sans font-medium uppercase tracking-wider text-xs transition-colors"
                            >
                                <WhatsappLogo size={16} weight="light" /> Chat on WhatsApp
                            </a>
                        </div>
                        <p className="font-sans text-xs text-white/35 mt-4">We reply within 24 hours. Usually much sooner.</p>
                    </motion.form>

                    {/* Info sidebar */}
                    <div className="lg:col-span-5 space-y-6" data-testid="contact-info">
                        <InfoRow icon={MapPin} label="Visit" value={SITE.address} />
                        <InfoRow icon={Phone} label="Call" value={SITE.whatsappDisplay} href={`tel:+${SITE.whatsappRaw}`} />
                        <InfoRow icon={EnvelopeSimple} label="Email" value={SITE.email} href={`mailto:${SITE.email}`} />
                        <InfoRow icon={WhatsappLogo} label="WhatsApp" value={SITE.whatsappDisplay} href={waLink()} external />

                        <div className="border border-white/10 p-8">
                            <div className="flex items-center gap-3 mb-4">
                                <Clock size={18} weight="light" className="text-gold" />
                                <p className="overline">Office Hours</p>
                            </div>
                            <p className="font-serif text-xl">Mon — Sat, 10:00 – 19:00 IST</p>
                            <p className="font-sans text-sm text-white/55 mt-2">Concierge available 24/7 for travelers currently on a trip with us.</p>
                        </div>

                        {/* Social */}
                        <div className="border border-white/10 p-8">
                            <p className="overline mb-5">Follow our journeys</p>
                            <div className="flex items-center gap-4">
                                <a href={SITE.social.instagram} target="_blank" rel="noreferrer" className="flex items-center gap-2 text-white/60 hover:text-gold transition-colors font-sans text-sm">
                                    <InstagramLogo size={18} weight="light" /> Instagram
                                </a>
                                <a href={SITE.social.facebook} target="_blank" rel="noreferrer" className="flex items-center gap-2 text-white/60 hover:text-gold transition-colors font-sans text-sm">
                                    <FacebookLogo size={18} weight="light" /> Facebook
                                </a>
                            </div>
                        </div>

                        {/* Map placeholder */}
                        <div className="border border-white/10 overflow-hidden">
                            <div className="bg-[#0F1730] p-6 flex items-center gap-3">
                                <MapPin size={18} weight="light" className="text-gold" />
                                <p className="font-sans text-sm text-white/70">{SITE.addressShort}</p>
                            </div>
                            <div className="h-48 bg-navy-surface flex items-center justify-center">
                                <a
                                    href={`https://maps.google.com/?q=${encodeURIComponent(SITE.address)}`}
                                    target="_blank"
                                    rel="noreferrer"
                                    className="inline-flex items-center gap-2 text-gold text-xs uppercase tracking-widest hover:text-[#E5C158]"
                                >
                                    Open in Google Maps <ArrowRight size={12} weight="bold" />
                                </a>
                            </div>
                        </div>
                    </div>
                </div>
            </section>
        </div>
    );
}

function Field({ label, name, value, onChange, type = "text", testid, placeholder = "", required = false }) {
    return (
        <div>
            <label className="overline block mb-3">{label}{required && <span className="text-gold ml-1">*</span>}</label>
            <input
                name={name}
                type={type}
                value={value}
                onChange={onChange}
                required={required}
                placeholder={placeholder}
                data-testid={testid}
                className="editorial-input w-full"
            />
        </div>
    );
}

function InfoRow({ icon: Icon, label, value, href, external }) {
    const inner = (
        <div className="flex items-start gap-5 group" data-testid={`contact-info-${label.toLowerCase()}`}>
            <div className="w-12 h-12 border border-gold/40 grid place-items-center shrink-0 group-hover:bg-gold/10 transition-colors">
                <Icon size={20} weight="light" className="text-gold" />
            </div>
            <div className="pt-1">
                <p className="overline">{label}</p>
                <p className="font-serif text-xl mt-2 text-white leading-snug">{value}</p>
            </div>
        </div>
    );
    if (!href) return inner;
    return (
        <a href={href} target={external ? "_blank" : undefined} rel={external ? "noreferrer" : undefined} className="block">
            {inner}
        </a>
    );
}
